import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { tsApi, type BreakEntry } from "../../../api/timesheets";
import { Button, TextInput } from "../../../components/ui";

const sel = "h-10 rounded-sm border border-white/15 bg-surface px-2 text-xs text-white";
const CLS: Record<string, string> = { UNPAID_MEAL: "Unpaid meal", PAID_SHORT: "Paid short", NEEDS_REVIEW: "Needs review" };

function BreakLine({ b, onChanged }: { b: BreakEntry; onChanged: () => void }) {
  const [mode, setMode] = useState<"" | "edit" | "void" | "classify">("");
  const [start, setStart] = useState(b.start_input);
  const [end, setEnd] = useState(b.end_input ?? "");
  const [text, setText] = useState("");
  const [cls, setCls] = useState("UNPAID");
  const go = async () => {
    try {
      if (mode === "edit") await tsApi.editBreak(b.id, { start_local: start, end_local: end || null, type: b.type, reason: text });
      if (mode === "void") await tsApi.voidBreak(b.id, text);
      if (mode === "classify") await tsApi.classify(b.id, cls, text);
      toast.success("Break updated");
      setMode("");
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const voided = b.status === "VOIDED";
  return (
    <li data-testid={`break-${b.id}`} className={`rounded-sm border border-white/10 p-3 text-xs ${voided ? "opacity-50" : ""}`}>
      <p className={voided ? "line-through" : "text-white"}>
        {b.start_local} → {b.end_local ?? "open"} {b.hms && <span className="font-mono">({b.hms})</span>} · {b.type} · {b.source}
        {" · "}<span data-testid={`break-class-${b.id}`} className={b.unpaid ? "text-sky-300" : "text-emerald-300"}>{b.admin_classification ? `Admin: ${b.admin_classification}` : b.classification ? CLS[b.classification] : "in progress"}</span>
      </p>
      {b.flags.length > 0 && <p className="font-mono text-[10px] text-warn">{b.flags.join(", ")}</p>}
      {voided && <p className="text-zinc-500">Voided: {b.void_reason}</p>}
      {!voided && !mode && (
        <div className="mt-2 flex gap-3">
          <button className="text-signal" onClick={() => setMode("edit")} data-testid={`break-edit-${b.id}`}>Edit</button>
          <button className="text-red-300" onClick={() => setMode("void")} data-testid={`break-void-${b.id}`}>Void</button>
          {b.classification === "NEEDS_REVIEW" && !b.admin_classification && <button className="text-amber-300" onClick={() => setMode("classify")} data-testid={`break-classify-${b.id}`}>Classify</button>}
        </div>
      )}
      {mode && (
        <div className="mt-2 space-y-2">
          {mode === "edit" && <div className="grid grid-cols-2 gap-2"><TextInput type="datetime-local" step={1} value={start} onChange={(e) => setStart(e.target.value)} data-testid="break-edit-start" /><TextInput type="datetime-local" step={1} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="break-edit-end" /></div>}
          {mode === "classify" && <select className={sel} value={cls} onChange={(e) => setCls(e.target.value)} data-testid="break-classify-select"><option value="UNPAID">Unpaid</option><option value="PAID">Paid</option></select>}
          <TextInput placeholder={mode === "classify" ? "Note (required)" : "Reason (required)"} value={text} onChange={(e) => setText(e.target.value)} data-testid="break-action-reason" />
          <div className="flex gap-2"><Button onClick={go} disabled={!text.trim()} data-testid="break-action-save">Save</Button><Button variant="ghost" onClick={() => setMode("")}>Cancel</Button></div>
        </div>
      )}
    </li>
  );
}

export function BreaksEditor({ sessionId, breaks, onChanged }: { sessionId: string; breaks: BreakEntry[]; onChanged: () => void }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [type, setType] = useState("UNCLASSIFIED");
  const add = async () => {
    try {
      await tsApi.addBreak(sessionId, { start_local: start, end_local: end || null, type });
      toast.success("Break added");
      setStart("");
      setEnd("");
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div data-testid="breaks-editor">
      <ul className="space-y-2">{breaks.map((b) => <BreakLine key={b.id} b={b} onChanged={onChanged} />)}</ul>
      {!breaks.length && <p className="text-xs text-zinc-500">No breaks.</p>}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <TextInput type="datetime-local" step={1} value={start} onChange={(e) => setStart(e.target.value)} data-testid="break-add-start" />
        <TextInput type="datetime-local" step={1} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="break-add-end" />
        <select className={sel} value={type} onChange={(e) => setType(e.target.value)} data-testid="break-add-type">{["UNCLASSIFIED", "MEAL", "REST"].map((t) => <option key={t}>{t}</option>)}</select>
        <Button onClick={add} disabled={!start} data-testid="break-add-btn">Add break</Button>
      </div>
      <p className="mt-1 text-[11px] text-zinc-500">Venue-local time. ≥30 min = unpaid meal, ≤20 min = paid, 21–29 min = needs review. Breaks are never deleted, only voided.</p>
    </div>
  );
}
